import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types'
import type { Branch } from '@/stores/auth-store'

type BranchRecord = Record<string, unknown>

export interface BranchGeneratedCredentials {
  userId?: string | number | null
  username?: string | null
  email?: string | null
  phone?: string | null
  name?: string | null
  password?: string | null
}

export interface BranchSupportProvisionResult {
  branch: Branch | null
  credentials: BranchGeneratedCredentials | null
}

const getRecordValue = (record: BranchRecord, ...keys: string[]) => {
  for (const key of keys) {
    const value = record[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }

  return undefined
}

const normalizeBranch = (record: BranchRecord): Branch => ({
  id: getRecordValue(record, 'id', 'instance_id') as string | number,
  name: String(getRecordValue(record, 'name', 'instance_id') ?? 'Unnamed branch'),
  serviceId: getRecordValue(record, 'service_id', 'instance_id') as string | undefined,
  branchId: getRecordValue(record, 'branch_id') as string | number | undefined,
  tenantId: getRecordValue(record, 'tenant_id') as string | number | undefined,
  primaryNode: getRecordValue(record, 'primary_node') as string | number | undefined,
  code: getRecordValue(record, 'identifier', 'instance_id') as string | undefined,
  instanceId: getRecordValue(record, 'instance_id', 'code') as string | undefined,
  parentInstanceId: getRecordValue(record, 'parent_instance_id') as string | undefined,
  identifier: getRecordValue(record, 'identifier', 'code') as string | undefined,
  address: getRecordValue(record, 'address', 'node_name') as string | undefined,
  nodeName: getRecordValue(record, 'node_name') as string | undefined,
  stateName: getRecordValue(record, 'state_name') as string | undefined,
  bannerUrl: getRecordValue(record, 'welcome_page_logo', 'side_panel_logo', 'login_page_logo') as string | undefined,
  logoUrl: getRecordValue(record, 'logo') as string | undefined,
  countryId: getRecordValue(record, 'country_id', 'country') as string | number | undefined,
  countryName: getRecordValue(record, 'country_name') as string | undefined,
  countryCode: getRecordValue(record, 'country_code') as string | undefined,
  timeZoneId: getRecordValue(record, 'time_zone_id') as string | number | undefined,
  timeZone: getRecordValue(record, 'time_zone', 'timezone') as string | undefined,
  currencySymbol: getRecordValue(record, 'currency_symbol') as string | undefined,
  currencyCode: getRecordValue(record, 'currency_code') as string | undefined,
  createdOn: getRecordValue(record, 'created_on') as string | undefined,
  createdByUserId: getRecordValue(record, 'created_by_user_id') as string | number | undefined,
  createdByName: getRecordValue(record, 'created_by_name') as string | undefined,
  status: getRecordValue(record, 'status') as string | number | undefined,
  serviceVariant: getRecordValue(record, 'service_variant') as string | number | undefined,
  serviceVariantName: getRecordValue(record, 'service_variant_name', 'service_variant_label') as string | undefined,
  branchOwnership: getRecordValue(record, 'branch_ownership') as string | number | undefined,
  branchOwnershipLabel: getRecordValue(record, 'branch_ownership_label') as string | undefined,
  isVendorBranch: Boolean(getRecordValue(record, 'is_vendor_branch')),
  isActive: Boolean(getRecordValue(record, 'is_active')),
  canDelete: Boolean(getRecordValue(record, 'can_delete')),
  usageCount: Number(getRecordValue(record, 'usage_count') ?? 0),
  linkedJobCount: Number(getRecordValue(record, 'linked_job_count') ?? 0),
  linkedLocationCount: Number(getRecordValue(record, 'linked_location_count') ?? 0),
  linkedWorkerCount: Number(getRecordValue(record, 'linked_worker_count') ?? 0),
  linkedRoleAssignmentCount: Number(getRecordValue(record, 'linked_role_assignment_count') ?? 0),
  linkedUserCount: Number(getRecordValue(record, 'linked_user_count') ?? 0),
  adminUserId: getRecordValue(record, 'admin_user_id') as string | number | undefined,
  adminName: getRecordValue(record, 'admin_name') as string | undefined,
  adminEmail: getRecordValue(record, 'admin_email') as string | undefined,
  adminPhone: getRecordValue(record, 'admin_phone') as string | undefined,
  supportUserId: getRecordValue(record, 'support_user_id') as string | number | undefined,
  supportName: getRecordValue(record, 'support_name') as string | undefined,
  supportEmail: getRecordValue(record, 'support_email') as string | undefined,
  supportPhone: getRecordValue(record, 'support_phone') as string | undefined,
  levelVariantName: getRecordValue(record, 'level_variant_name') as string | undefined,
  levelVariantTierCode: getRecordValue(record, 'level_variant_tier_code') as string | undefined,
})

const normalizeGeneratedCredentials = (record: BranchRecord): BranchGeneratedCredentials | null => {
  const credentials = record.credentials
  if (!credentials || typeof credentials !== 'object') {
    return null
  }

  const entry = credentials as BranchRecord
  return {
    userId: getRecordValue(entry, 'user_id') as string | number | undefined,
    username: getRecordValue(entry, 'username') as string | undefined,
    email: getRecordValue(entry, 'email') as string | undefined,
    phone: getRecordValue(entry, 'phone') as string | undefined,
    name: getRecordValue(entry, 'name') as string | undefined,
    password: getRecordValue(entry, 'password') as string | undefined,
  }
}

export interface CreateBranchPayload {
  name: string
  service_variant: string
  branch_ownership: string
  owner_user_id?: string
  admin_user_id?: string
}

export const branchesApi = {
  list: async (options?: {
    includeInactive?: boolean
    serviceInstanceId?: string | number | null
    uniqueLevelVariantId?: string | number | null
  }): Promise<Branch[]> => {
    const serviceInstanceId = String(options?.serviceInstanceId ?? '').trim()
    const uniqueLevelVariantId = String(options?.uniqueLevelVariantId ?? '').trim()

    const response = await api.get<ApiResponse<BranchRecord[]> | BranchRecord[]>(API_PATHS.organisation.branches.list, {
      params: {
        ...(options?.includeInactive ? { include_inactive: 1 } : {}),
        ...(serviceInstanceId ? { service_instance_id: serviceInstanceId } : {}),
        ...(uniqueLevelVariantId ? { unique_level_variant_id: uniqueLevelVariantId } : {}),
      },
    })
    const payload = Array.isArray(response.data)
      ? response.data
      : response.data.data

    return Array.isArray(payload) ? payload.map((branch) => normalizeBranch(branch)) : []
  },

  create: async (payload: CreateBranchPayload): Promise<unknown> => {
    const response = await api.post(API_PATHS.organisation.branches.create, payload)
    return response.data
  },

  updateAdmin: async (id: string | number, userId: string | number): Promise<Branch | null> => {
    const response = await api.post<ApiResponse<BranchRecord> | BranchRecord>(
      API_PATHS.organisation.branches.updateAdmin(id),
      { user_id: userId },
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : typeof response.data === 'object' && response.data !== null && 'data' in response.data
        ? response.data.data
        : response.data

    return payload && typeof payload === 'object' ? normalizeBranch(payload as BranchRecord) : null
  },

  assignSupportAdmin: async (id: string | number): Promise<BranchSupportProvisionResult> => {
    const response = await api.post<ApiResponse<BranchRecord> | BranchRecord>(
      API_PATHS.organisation.branches.supportAdmin(id),
      {},
    )
    const payload = Array.isArray(response.data)
      ? response.data[0]
      : typeof response.data === 'object' && response.data !== null && 'data' in response.data
        ? response.data.data
        : response.data

    if (!payload || typeof payload !== 'object') {
      return { branch: null, credentials: null }
    }

    return {
      branch: normalizeBranch(payload as BranchRecord),
      credentials: normalizeGeneratedCredentials(payload as BranchRecord),
    }
  },

  remove: async (id: string | number): Promise<unknown> => {
    const response = await api.delete(API_PATHS.organisation.branches.remove(id))
    return response.data
  },

  setActiveState: async ({ id, isActive }: { id: string | number; isActive: boolean }): Promise<unknown> => {
    const response = await api.post(
      isActive
        ? API_PATHS.organisation.branches.activate(id)
        : API_PATHS.organisation.branches.suspend(id),
    )
    return response.data
  },
}